// INDEXUL INVERS al elementelor (mock-ui/structura-ierarhie-produs.md §3):
// azi elementul trăiește doar ca `?focus=` peste pagina unui pas; aici devine
// obiect navigabil. Indexul se construiește ÎN CLIENT, din rezultatele deja
// aduse ale Pașilor 13–16 — fiecare element vine cu lista lui de verificări
// (E, R, R/E, verdict), deci nu e nevoie de nicio rută nouă pe server.
//
// Vocabularul verdictelor (unic, ca în mock): «SE VERIFICA…» = conform,
// «ATENTIE…» = la limită (trece, dar cu sub ~9 % rezervă), restul = neconform.
// «DECIZIE» nu e un verdict: rândul e o decizie de proiect, fără E/R, care nu
// trece și nu pică, deci nu stă printre verificări (`decizii`).

import { cheiaFundatiei, fundatiaAdoptata } from "./fundatia";
import { StepState } from "./tipuri";
import { numar, t, tn } from "../i18n";
import type { Cheie } from "../i18n";

export type VerdictElement = "conform" | "la_limita" | "neconform";

export type VerificareElement = {
  nume: string;
  punct?: string | null;
  grupa?: string | null;
  E?: number | string | null;
  R?: number | string | null;
  RE: number | null;
  verdict: string;
  referinta?: string | null;
  acceptatMarja?: number | null;
  unitate?: string | null;
  nota?: string | null;
  // câmpurile brute pe care pasul le pune lângă verdict (valorile
  // intermediare): se arată la EXPANDAREA rândului, ca detaliul să nu mai
  // ceară un drum până în pagina pasului
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  detaliu?: Record<string, any> | null;
};

/** O decizie de proiect publicată de un pas lângă verificări (verdictul
 *  `VERDICT_DECIZIE`): ce rămâne de ales proiectantului și cifrele cu care
 *  alege, de pildă elementul de întrerupere a punții termice a consolei. */
export type DecizieElement = {
  nume: string;
  punct?: string | null;
  referinta?: string | null;
  nota?: string | null;
};

export type ElementIndexat = {
  ref: string; // id-ul de plan: GR1.2, ST11.3, PB4, OP7.2 sau numele axului
  tip: "grinda" | "stalp" | "lamela" | "perete" | "ochi" | "ax" | "placa_sol"
    | "zidarie";
  nivel: string;
  sectiune: string; // „30 × 50", „h 15 cm"…
  detaliu?: string; // L, beton etc., pentru antetul paginii
  verdict: VerdictElement;
  verdictBrut: string;
  reMin: number | null;
  criteriu: string; // numele verificării care guvernează (R/E minim)
  pas: string; // cheia pasului care armează elementul
  otelKg?: number | null;
  betonM3?: number | null;
  verificari: VerificareElement[];
  // deciziile de proiect ale elementului: NU intră în verdict și în numărători
  decizii?: DecizieElement[];
  istoric?: { de_ce: string; de_la: string; la: string; re: number }[];
  // înregistrarea BRUTĂ a elementului, așa cum a venit de la pasul lui:
  // pagina elementului o dă desenului de secțiune (care o cere tipată), fără
  // s-o remodeleze — orice câmp nou al serverului ajunge acolo automat
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  brut?: any;
  // înregistrarea PĂRINTE, când elementul e un rând din ea: la stâlpi, `brut`
  // e nivelul, iar vederea 3D cere stiva întreagă (toate nivelurile)
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  brutParinte?: any;
  // parametrii pasului care a produs elementul (acoperirea, betonul…): de ei
  // au nevoie desenele ca să pună cotele corecte
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  param?: any;
};

/** Numele felului de element, ca CHEIE de catalog: ecranele îl trec prin `t`
 *  al lor, ca schimbarea limbii să le redeseneze (v. `docs/internationalizare.md`). */
export const TIP_ETICHETA: Record<ElementIndexat["tip"], Cheie> = {
  grinda: "struct.tip.grinda",
  stalp: "struct.tip.stalp",
  lamela: "struct.tip.lamela",
  perete: "struct.tip.perete",
  ochi: "struct.tip.ochi",
  ax: "struct.tip.ax",
  placa_sol: "struct.tip.placa_sol",
  zidarie: "struct.tip.zidarie",
};

export function normalizeazaVerdict(brut: string): VerdictElement {
  const v = String(brut ?? "");
  if (v.startsWith("SE VERIFICA")) return "conform";
  if (v.startsWith("ATENTIE")) return "la_limita";
  return "neconform";
}

/** Banda «de revizuit» a PREZENTĂRII (`optimizare.REVIEW_RE_MIN/MAX` pe
 *  server): o verificare picată cu R/E de la 0,90 în sus și una trecută cu
 *  R/E până la 1,05 se arată «la limită», nu roșu/verde. Numai pe ecran:
 *  verdictul soluției, scorul și țintele solverului rămân pe pragul de 100 %,
 *  iar verdictul brut al pasului stă neschimbat în `verdictBrut`. */
export const REVIEW_RE_MIN = 0.9;
export const REVIEW_RE_MAX = 1.05;

export function verdictPrezentat(brut: string, RE: number | null | undefined): string {
  const v = String(brut ?? "");
  if (typeof RE !== "number" || !isFinite(RE)) return v;
  if (!v.startsWith("SE VERIFICA") && !v.startsWith("ATENTIE") && RE >= REVIEW_RE_MIN)
    return "ATENTIE!!!";
  if (v.startsWith("SE VERIFICA") && RE <= REVIEW_RE_MAX) return "ATENTIE!!!";
  return v;
}

/** Verdictul unui element pe ecran: cel mai rău dintre verificările lui, așa
 *  cum se ARATĂ ele (cu banda de revizuit); fără verificări, cel brut. */
export function verdictDinVerificari(
  verificari: VerificareElement[], brut: string
): VerdictElement {
  if (!verificari.length) return normalizeazaVerdict(brut);
  let rau: VerdictElement = "conform";
  for (const v of verificari) {
    const n = normalizeazaVerdict(v.verdict);
    if (n === "neconform") return "neconform";
    if (n === "la_limita") rau = "la_limita";
  }
  return rau;
}

// Verdictele pe care pașii le scriu în bază, în forma foii de calcul. Ele
// rămân cheia după care se alege culoarea (`startsWith("SE VERIFICA")`);
// pe ecran se scrie traducerea lor.
const VERDICT_SERVER: Record<string, Cheie> = {
  "SE VERIFICA!": "struct.verdict.se_verifica",
  "NU SE VERIFICA!!!": "struct.verdict.nu_se_verifica",
  "ATENTIE!!!": "struct.verdict.atentie",
  "ATENTIE": "struct.verdict.atentie_scurt",
};

/** Verdictul brut al unui pas, în limba ecranului; orice altă formă trece
 *  neatinsă. */
export function textVerdict(brut: unknown): string {
  const v = String(brut ?? "");
  const cheie = VERDICT_SERVER[v];
  return cheie ? t(cheie) : v;
}

export const VERDICT_CHIP: Record<VerdictElement, { cls: string; text: Cheie }> = {
  conform: { cls: "ob-c-ok", text: "struct.verdict.conform" },
  la_limita: { cls: "ob-c-amb", text: "struct.verdict.la_limita" },
  neconform: { cls: "ob-c-bad", text: "struct.verdict.neconform" },
};

// R/E minim + verificarea care îl dă. Verificările constructive pot avea
// RE null (E/R sunt texte) — nu intră în minim.
function reMinim(vs: VerificareElement[]): { re: number | null; criteriu: string } {
  let re: number | null = null;
  let criteriu = "";
  for (const v of vs) {
    if (v.RE == null || !isFinite(v.RE)) continue;
    if (re == null || v.RE < re) {
      re = v.RE;
      criteriu = v.nume;
    }
  }
  return { re, criteriu };
}

/** Verdictul cu care un pas pune o decizie de proiect în lista lui de
 *  verificări (Pasul 16: `armare_placa.verif_consola`). */
export const VERDICT_DECIZIE = "DECIZIE";

export function eDecizie(v: { verdict?: unknown }): boolean {
  return String(v?.verdict ?? "") === VERDICT_DECIZIE;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function mapVerificari(brute: any[] | null | undefined): VerificareElement[] {
  return (brute ?? []).filter((v: any) => !eDecizie(v)).map((v: any) => ({
    nume: String(v.nume ?? ""),
    punct: v.punct ?? null,
    grupa: v.grupa ?? null,
    E: v.E ?? null,
    R: v.R ?? null,
    RE: typeof v.RE === "number" ? v.RE : null,
    verdict: verdictPrezentat(String(v.verdict ?? ""),
                              typeof v.RE === "number" ? v.RE : null),
    referinta: v.referinta ?? null,
    acceptatMarja: v.acceptat_marja ?? null,
    unitate: v.unitate ?? null,
    nota: v.nota ?? null,
    detaliu: v.detaliu ?? null,
  }));
}

function mapDecizii(brute: any[] | null | undefined): DecizieElement[] {
  return (brute ?? []).filter(eDecizie).map((v: any) => ({
    nume: String(v.nume ?? ""),
    punct: v.punct ?? null,
    referinta: v.referinta ?? null,
    nota: v.nota ?? null,
  }));
}

// Construiește tot indexul din rezultatele pașilor. Citirea e DEFENSIVĂ:
// orice pas nerulat sau cu altă formă (rulare veche) e sărit, nu aruncat.
export function indexElemente(steps: Record<string, StepState>): ElementIndexat[] {
  const out: ElementIndexat[] = [];

  // P13 — grinzile
  const g = steps["armare_grinzi"]?.result as any;
  for (const gr of g?.grinzi ?? []) {
    const verificari = mapVerificari(gr.verificari);
    const { re, criteriu } = reMinim(verificari);
    out.push({
      ref: String(gr.element ?? ""),
      tip: "grinda",
      nivel: String(gr.nivel ?? ""),
      sectiune: gr.b_cm && gr.h_cm ? `${gr.b_cm} × ${gr.h_cm}` : "—",
      detaliu: gr.L_ax_m ? t("struct.elem.det_L", { n: nr(gr.L_ax_m, 2) }) : undefined,
      verdict: verdictDinVerificari(verificari, gr.verdict),
      verdictBrut: String(gr.verdict ?? ""),
      reMin: re,
      criteriu,
      pas: "armare_grinzi",
      verificari,
      decizii: mapDecizii(gr.verificari),
      brut: gr,
      param: g?.parametri,
    });
  }

  // P15 — stâlpii: stack-ul are câte un rând PER NIVEL (id_plan „ST11.3")
  const s = steps["armare_stalpi"]?.result as any;
  for (const st of s?.stalpi ?? []) {
    for (const niv of st.nivele ?? []) {
      const verificari = mapVerificari(niv.verificari);
      const { re, criteriu } = reMinim(verificari);
      const reFinal = niv.re_min ?? re;
      out.push({
        ref: String(niv.id_plan ?? st.element ?? ""),
        tip: "stalp",
        nivel: String(niv.nivel ?? ""),
        sectiune: niv.b_cm && niv.h_cm ? `${niv.b_cm} × ${niv.h_cm}` : "—",
        detaliu: niv.H_m ? t("struct.elem.det_H", { n: nr(niv.H_m, 2) }) : undefined,
        verdict: verdictDinVerificari(verificari, niv.verdict),
        verdictBrut: String(niv.verdict ?? ""),
        reMin: typeof reFinal === "number" ? reFinal : re,
        criteriu: criteriu || t("struct.elem.criteriu_pmm"),
        pas: "armare_stalpi",
        verificari,
        decizii: mapDecizii(niv.verificari),
        brut: niv,
        brutParinte: st,
        param: s?.parametri,
      });
    }
  }

  // P14 — pereții de subsol
  const p = steps["armare_pereti"]?.result as any;
  for (const pe of p?.pereti ?? []) {
    const verificari = mapVerificari(pe.verificari);
    const { re, criteriu } = reMinim(verificari);
    out.push({
      ref: String(pe.element ?? ""),
      tip: "perete",
      nivel: String(pe.nivel ?? ""),
      // grosimea peretelui e `t_cm`; `h_cm` e ÎNĂLȚIMEA lui (panoul de subsol
      // are 280 cm înălțime și 25 cm grosime — nu se confundă)
      sectiune: pe.t_cm ? t("struct.elem.sect_grosime", { n: nr(pe.t_cm) }) : "—",
      detaliu:
        (pe.L_ax_m ? t("struct.elem.det_L", { n: nr(pe.L_ax_m, 2) }) : "") +
        (pe.h_cm ? ` · ${t("struct.elem.det_h", { n: nr(pe.h_cm) })}` : ""),
      verdict: verdictDinVerificari(verificari, pe.verdict),
      verdictBrut: String(pe.verdict ?? ""),
      reMin: re,
      criteriu,
      pas: "armare_pereti",
      verificari,
      decizii: mapDecizii(pe.verificari),
      brut: pe,
      param: p?.parametri,
    });
  }

  // P14 — lamelele: elementele verticale cu latura lungă ≥ 4·b, armate ca
  // pereți structurali. Id-ul e al stâlpilor („ST12.3"), dar pasul care le
  // armează e al pereților.
  for (const la of p?.lamele ?? []) {
    const verificari = mapVerificari(la.verificari);
    const { re, criteriu } = reMinim(verificari);
    out.push({
      ref: String(la.element ?? ""),
      tip: "lamela",
      nivel: String(la.nivel ?? ""),
      sectiune: la.t_cm && la.L_cm ? `${la.t_cm} × ${la.L_cm}` : "—",
      detaliu: la.H_m ? t("struct.elem.det_H", { n: nr(la.H_m, 2) }) : undefined,
      verdict: verdictDinVerificari(verificari, la.verdict),
      verdictBrut: String(la.verdict ?? ""),
      reMin: typeof la.re_min === "number" ? la.re_min : re,
      criteriu,
      pas: "armare_pereti",
      verificari,
      decizii: mapDecizii(la.verificari),
      brut: la,
      // STIVA lamelei: nivelurile ei și rândul poziției (zona A, l_cr) —
      // elevația cotată le desenează pe toate, cu nivelul paginii evidențiat
      brutParinte: {
        stack: la.stack,
        nivele: (p?.lamele ?? []).filter((x: any) => x.stack === la.stack),
        stiva: (p?.stive_lamele ?? []).find((x: any) => x.stack === la.stack) ?? null,
      },
      param: p?.parametri_lamele,
    });
  }

  // pereții de zidărie confinată ai proeminenței (ZC1.5, ZC2.5…): vin din
  // modelul de calcul, nu dintr-un pas de armare — fișa lor are tipul,
  // nivelul, grosimea și lungimea, fără verificări proprii
  const femZc = (steps["model_fem"]?.result as any)?.model?.pereti_zidarie ?? [];
  for (const zc of femZc) {
    out.push({
      ref: String(zc.id_plan ?? ""),
      tip: "zidarie",
      nivel: String(zc.nivel ?? ""),
      sectiune: zc.t_m && zc.L_m
        ? `${nr(zc.t_m * 100)} × ${nr(zc.L_m * 100)}` : "—",
      detaliu: zc.L_m ? t("struct.elem.det_L", { n: nr(zc.L_m, 2) }) : undefined,
      verdict: "conform",
      verdictBrut: "",
      reMin: null,
      criteriu: "",
      pas: "model_fem",
      verificari: [],
      brut: zc,
    });
  }

  // P16 — ochiurile de placă
  const o = steps["armare_placa"]?.result as any;
  for (const oc of o?.panouri ?? []) {
    const verificari = mapVerificari(oc.verificari);
    const { re, criteriu } = reMinim(verificari);
    out.push({
      ref: String(oc.id ?? ""),
      tip: "ochi",
      nivel: String(oc.nivel_nume ?? oc.nivel ?? ""),
      sectiune: oc.h_cm ? t("struct.elem.sect_placa", { n: oc.h_cm }) : "—",
      detaliu: oc.arie_m2 ? `${nr(oc.arie_m2, 1)} m²` : undefined,
      verdict: verdictDinVerificari(verificari, oc.verdict),
      verdictBrut: String(oc.verdict ?? ""),
      reMin: re,
      criteriu,
      pas: "armare_placa",
      otelKg: oc.otel_kg ?? null,
      betonM3: oc.beton_m3 ?? null,
      verificari,
      decizii: mapDecizii(oc.verificari),
      istoric: oc.istoric ?? undefined,
      brut: oc,
      param: o?.parametri,
    });
  }

  // P17 — AXELE de fundație. Verificările nu vin ca listă gata făcută (pasul
  // le ține ca praguri per combinație), deci le compunem aici din cele trei
  // lecturi ale presiunii + desprindere + tasare — aceleași cifre pe care le
  // arată cardul din pagina fundației, ca elementul „ax" să aibă exact
  // formatul celorlalte.
  const f = fundatiaAdoptata(steps) as any;
  const pasFundatie = cheiaFundatiei(steps);
  const ver = f?.verificari;
  // P18 — armarea aceluiași ax. Axul rămâne UN SINGUR element: verificările
  // de teren (Pasul 17) și cele de armătură (Pasul 18) stau împreună, ca la
  // stâlpi și grinzi, unde secțiunea și armătura n-au două pagini separate.
  const af = steps["armare_fundatie"]?.result as any;
  const armarePeAx: Record<string, any> = {};
  for (const a of af?.axe ?? []) armarePeAx[String(a.ax ?? "")] = a;
  if (ver?.stare === "calculat") {
    const tasareLim = ver?.global?.tasare_absoluta_limita_cm;
    for (const ax of ver.axe ?? []) {
      const arm = armarePeAx[String(ax.nume ?? "")];
      const vs: VerificareElement[] = mapVerificari(arm?.verificari);
      // cea mai defavorabilă combinație pentru fiecare lectură
      const lect = (
        nume: string,
        p: (c: any) => number | null | undefined,
        prag: (c: any) => number | null | undefined,
        unitate = "kPa"
      ) => {
        let best: { p: number; prag: number; combo: string } | null = null;
        for (const c of ax.combinatii ?? []) {
          const pv = p(c);
          const pr = prag(c);
          if (pv == null || pr == null || pv <= 0) continue;
          const re = pr / pv;
          if (!best || re < best.prag / best.p) best = { p: pv, prag: pr, combo: c.combinatie };
        }
        if (!best) return;
        const re = best.prag / best.p;
        vs.push({
          nume,
          punct: best.combo,
          E: `${nr(best.p, 1)} ${unitate}`,
          R: `${nr(best.prag, 1)} ${unitate}`,
          RE: re,
          verdict: re >= 1 ? "SE VERIFICA" : "NU SE VERIFICA",
        });
      };
      lect(t("struct.elem.ver_presiune_birou"), (c) => c.p1_kpa, (c) => c.prag_metoda_kpa);
      lect(t("struct.elem.ver_presiune_np112"), (c) => c.p_max_np112_kpa, (c) => c.prag_np112_kpa);
      if (ax.ok_desprindere != null)
        vs.push({
          nume: t("struct.elem.ver_desprindere"),
          punct: ax.guverneaza ?? null,
          E: ax.zona_activa_m != null
            ? t("struct.elem.desprindere_activ", { n: nr(ax.zona_activa_m, 2) })
            : null,
          R: ax.B_m != null ? t("struct.elem.det_B", { n: nr(ax.B_m, 2) }) : null,
          RE: null,
          verdict: ax.ok_desprindere ? "SE VERIFICA" : "NU SE VERIFICA",
        });
      if (ax.tasare_cm != null && tasareLim)
        vs.push({
          nume: t("struct.elem.ver_tasare"),
          E: `${nr(ax.tasare_cm, 1)} cm`,
          R: `${nr(tasareLim, 1)} cm`,
          RE: tasareLim / Math.max(ax.tasare_cm, 1e-6),
          verdict: ax.tasare_cm <= tasareLim ? "SE VERIFICA" : "NU SE VERIFICA",
        });
      if (ax.ok_model != null)
        vs.push({
          nume: t("struct.elem.ver_presiune_model"),
          E: ax.p_max_model_kpa != null ? `${nr(ax.p_max_model_kpa, 1)} kPa` : null,
          R: null,
          RE: null,
          verdict: ax.ok_model ? "SE VERIFICA" : "NU SE VERIFICA",
        });
      const { re, criteriu } = reMinim(vs);
      const pica = vs.some((v) => normalizeazaVerdict(v.verdict) === "neconform");
      out.push({
        ref: String(ax.nume ?? ""),
        tip: "ax",
        nivel: ax.directie
          ? t("struct.elem.niv_directie", { dir: ax.directie })
          : t("struct.elem.niv_fundatie"),
        sectiune: ax.B_m ? t("struct.elem.det_B", { n: nr(ax.B_m, 2) }) : "—",
        detaliu:
          (ax.L_m ? t("struct.elem.det_L", { n: nr(ax.L_m, 1) }) : "") +
          (ax.nr_stalpi ? ` · ${tn("struct.elem.det_stalpi#n", ax.nr_stalpi)}` : ""),
        verdict: pica ? "neconform" : re != null && re < 1.1 ? "la_limita" : "conform",
        verdictBrut: pica ? "NU SE VERIFICA" : "SE VERIFICA",
        reMin: re,
        criteriu,
        // pagina de armare, când pasul a rulat: acolo sunt barele, secțiunea
        // desenată și diagramele. Altfel rămâne pagina fundației.
        pas: arm ? "armare_fundatie" : pasFundatie,
        otelKg: arm?.otel_kg ?? null,
        betonM3:
          arm && arm.L_m
            ? Math.round(
                arm.L_m *
                  (arm.B_m * arm.h_talpa_m +
                    arm.b_inima_m * Math.max(arm.H_c_m - arm.h_talpa_m, 0)) *
                  10
              ) / 10
            : null,
        verificari: vs,
        decizii: mapDecizii(arm?.verificari),
        brut: arm ? { ...ax, armare: arm } : ax,
        param: f?.sectiune_fundatie,
      });
    }
  }
  // AXELE ARMATE FĂRĂ PERECHE în Pasul 17 (acolo axul are alt nume, sau
  // Pasul 17 n-a calculat): tot elemente, cu pagina lor — sunt membrii
  // claselor de grindă de fundare din «Componente», iar un link spre ele nu
  // are voie să ducă la «negăsit»
  const numeP17 = new Set(
    ver?.stare === "calculat" ? (ver.axe ?? []).map((x: any) => String(x.nume ?? "")) : []);
  for (const a of af?.axe ?? []) {
    const nume = String(a.ax ?? "");
    if (!nume || numeP17.has(nume)) continue;
    const vs = mapVerificari(a.verificari);
    const { re, criteriu } = reMinim(vs);
    out.push({
      ref: nume,
      tip: "ax",
      nivel: t("struct.elem.niv_fundatie"),
      sectiune: a.B_m ? t("struct.elem.det_B", { n: nr(a.B_m, 2) }) : "—",
      detaliu: a.L_m ? t("struct.elem.det_L", { n: nr(a.L_m, 1) }) : undefined,
      verdict: normalizeazaVerdict(String(a.verdict ?? "")),
      verdictBrut: String(a.verdict ?? ""),
      reMin: re,
      criteriu,
      pas: "armare_fundatie",
      otelKg: a.otel_kg ?? null,
      betonM3: a.L_m
        ? Math.round(a.L_m * (a.B_m * a.h_talpa_m
            + a.b_inima_m * Math.max(a.H_c_m - a.h_talpa_m, 0)) * 10) / 10
        : null,
      verificari: vs,
      decizii: mapDecizii(a.verificari),
      brut: { nume, ax: nume, B_m: a.B_m, L_m: a.L_m, armare: a },
      param: f?.sectiune_fundatie,
    });
  }

  // PLACA PE SOL — pardoseala nivelului de jos. Nu e planșeu (reazemă pe
  // teren pe toată suprafața, nu intră în modelul seismic), dar e o piesă
  // turnată și armată ca oricare alta: are id, cantități, verificări și
  // pagină. Fără ea, un click pe placa din 3D nu ducea nicăieri.
  const psol = af?.placa_pe_sol;
  if (psol?.activ) {
    const modelFem = (steps["model_fem"]?.result as any)?.model;
    const nivel = String(modelFem?.placa_pe_sol?.nivel ?? t("struct.elem.niv_jos"));
    const arieTot = Number(psol.arie_m2) || 0;
    const bucati = (psol.elemente?.length ? psol.elemente
                    : [{ element: "PS1", arie_m2: arieTot }]) as any[];
    for (const b of bucati) {
      const arie = Number(b.arie_m2) || 0;
      // cantitățile componentei, proporțional cu aria ei din amprentă
      const cota = arieTot > 0 ? arie / arieTot : 1;
      const vs = mapVerificari(psol.verificari);
      const { re, criteriu } = reMinim(vs);
      const pica = vs.some((v) => normalizeazaVerdict(v.verdict) === "neconform");
      out.push({
        ref: String(b.element ?? "PS1"),
        tip: "placa_sol",
        nivel,
        sectiune: t("struct.elem.det_h", { n: nr((psol.t_m ?? 0) * 100) }),
        detaliu:
          `${nr(arie, 1)} m²` +
          (psol.plasa?.text ? ` · ${psol.plasa.text}` : ""),
        verdict: pica ? "neconform" : re != null && re < 1.1 ? "la_limita" : "conform",
        verdictBrut: pica ? "NU SE VERIFICA" : "SE VERIFICA",
        reMin: re,
        criteriu,
        pas: "armare_fundatie",
        otelKg: psol.otel_kg != null
          ? Math.round(Number(psol.otel_kg) * cota) : null,
        betonM3: psol.beton_m3 != null
          ? Math.round(Number(psol.beton_m3) * cota * 10) / 10 : null,
        verificari: vs,
        decizii: mapDecizii(psol.verificari),
        brut: {
          ...psol,
          element: b.element,
          arie_m2: arie,
          beton_m3: psol.beton_m3 != null
            ? Math.round(Number(psol.beton_m3) * cota * 100) / 100 : null,
          otel_kg: psol.otel_kg != null
            ? Math.round(Number(psol.otel_kg) * cota) : null,
        },
      });
    }
  }

  return out;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Un nivel e SUBTERAN dacă numele lui conține «subsol» sau «demisol» —
 *  aceeași convenție ca pe server (`extras_armare.harta_niveluri`,
 *  `fem_shell._e_subteran`), ca zonele să nu se despartă între desen și
 *  deviz. */
export function esteSubteran(nivel: string | null | undefined): boolean {
  return /subsol|demisol/i.test(nivel ?? "");
}

/** Indexul PARTERULUI într-o listă de niveluri ordonată de jos în sus: primul
 *  nivel care nu e subteran (0 dacă toate sunt). Vederile se deschid acolo —
 *  parterul e nivelul despre care vorbește lumea. */
export function indexParter(
  niveluri: { nivel: string }[] | null | undefined
): number {
  const i = (niveluri ?? []).findIndex((n) => !esteSubteran(n.nivel));
  return i < 0 ? 0 : i;
}

export function nr(v: number, zecimale = 0): string {
  return numar(v, {
    minimumFractionDigits: zecimale,
    maximumFractionDigits: zecimale,
  });
}

// elementul cu referința dată; la stâlpi acceptă și stack-ul („ST11" găsește
// primul nivel) ca linkurile fără sufix să ducă undeva
export function gasesteElement(
  idx: ElementIndexat[],
  ref: string
): ElementIndexat | null {
  return (
    idx.find((e) => e.ref === ref) ??
    idx.find((e) => e.ref.split(".")[0] === ref) ??
    null
  );
}
